/**
 * Pure billing rules shared by the billing edge functions. No Deno, Stripe or
 * Supabase imports, so vitest can test every rule here
 * (src/lib/billing/__tests__/stripeBilling.test.ts). The plans come from
 * `plans.json`, the mirror of src/lib/pricing/plans.ts.
 */

export type PlanId = "start" | "firma";
/** Monthly billing only (Petr, 23. 10. 2026: no yearly prices). */
export type BillingInterval = "month";

export interface PlanLimits {
  companies: number;
  fioAccounts: number;
  users: number;
  aiDocumentsPerMonth: number;
}

export interface MirroredPlan {
  id: PlanId;
  name: string;
  draft: boolean;
  priceMonthlyCzk: number;
  trialDays: number | null;
  limits: PlanLimits;
  features: string[];
}

export interface PlansMirror {
  vatPayer: boolean;
  plans: MirroredPlan[];
}

export const PLAN_IDS: readonly PlanId[] = ["start", "firma"];
export const INTERVALS: readonly BillingInterval[] = ["month"];

export function isPlanId(value: unknown): value is PlanId {
  return value === "start" || value === "firma";
}

export function isInterval(value: unknown): value is BillingInterval {
  return value === "month";
}

// ---------------------------------------------------------------------------
// Lookup keys: the code names prices by lookup key, never by price id, so
// test and live mode differ only by the API key. Two prices: one per plan.
// ---------------------------------------------------------------------------

/** `start_monthly`, `firma_monthly`. */
export function lookupKeyFor(plan: PlanId): string {
  return `${plan}_monthly`;
}

/**
 * A Zettl price's plan from its lookup key. Anything else, retired yearly keys
 * included, is null: the webhook then falls back to the stored row.
 */
export function parseLookupKey(
  key: string | null | undefined
): { plan: PlanId; interval: BillingInterval } | null {
  const match = /^(start|firma)_monthly$/.exec(key ?? "");
  if (!match) return null;
  return { plan: match[1] as PlanId, interval: "month" };
}

/** The monthly price Stripe must charge, in haléře (CZK minor units). */
export function expectedUnitAmount(mirror: PlansMirror, plan: PlanId): number {
  return mirrorPlan(mirror, plan).priceMonthlyCzk * 100;
}

export function mirrorPlan(mirror: PlansMirror, plan: PlanId): MirroredPlan {
  const p = mirror.plans.find((x) => x.id === plan);
  if (!p) throw new Error(`Unknown plan: ${plan}`);
  return p;
}

/** Refuse to sell a price that drifted from plans.ts (wrong amount, currency or period). */
export function priceMatchesPlan(
  mirror: PlansMirror,
  plan: PlanId,
  price: {
    unit_amount: number | null;
    currency: string;
    recurring: { interval: string; interval_count: number } | null;
  }
): boolean {
  return (
    price.unit_amount === expectedUnitAmount(mirror, plan) &&
    price.currency.toLowerCase() === "czk" &&
    price.recurring?.interval === "month" &&
    price.recurring?.interval_count === 1
  );
}

// ---------------------------------------------------------------------------
// Stripe status → our state
// ---------------------------------------------------------------------------

/**
 * - `active` / `trialing`: full access
 * - `grace`: a payment failed and Stripe is retrying; full access for
 *   PAST_DUE_GRACE_DAYS, then read-only (org_plan() applies the window)
 * - `pending`: the first payment hasn't gone through yet
 * - `ended`: canceled, unpaid, expired or paused: read-only
 */
export type SubscriptionState = "active" | "trialing" | "grace" | "pending" | "ended";

export function subscriptionState(status: string): SubscriptionState {
  switch (status) {
    case "active":
      return "active";
    case "trialing":
      return "trialing";
    case "past_due":
      return "grace";
    case "incomplete":
      return "pending";
    default:
      // canceled, unpaid, incomplete_expired, paused, and anything new
      return "ended";
  }
}

/** Statuses that keep an org on its plan (org_plan() step 2). */
export const ENTITLING_STATUSES: readonly string[] = ["active", "trialing", "past_due"];

/** How long `past_due` keeps full access. Equal to the interval in org_plan() (tested). */
export const PAST_DUE_GRACE_DAYS = 21;

// ---------------------------------------------------------------------------
// Trial carry-over: paying during the app trial keeps the remaining days
// ---------------------------------------------------------------------------

/** Stripe requires Checkout's `subscription_data.trial_end` to be at least 48 h ahead. */
export const MIN_TRIAL_CARRY_HOURS = 48;
/** Slack on top, because the customer may sit on the Checkout page a while. */
export const TRIAL_CARRY_SLACK_MINUTES = 60;

/**
 * The `trial_end` (Unix seconds) to pass to Checkout, or null to charge at
 * once: with less than 48 h (+ slack) of the trial left, Stripe would refuse it.
 */
export function trialCarryOver(trialEndsAt: Date | null, now: Date): number | null {
  if (!trialEndsAt) return null;
  const minMs = (MIN_TRIAL_CARRY_HOURS * 60 + TRIAL_CARRY_SLACK_MINUTES) * 60_000;
  if (trialEndsAt.getTime() - now.getTime() < minMs) return null;
  return Math.floor(trialEndsAt.getTime() / 1000);
}

// ---------------------------------------------------------------------------
// Out-of-order guard (mirrors the WHERE clause of billing_upsert_subscription)
// ---------------------------------------------------------------------------

/** Write a fetched snapshot only when it is newer than the stored one. */
export function shouldApplySnapshot(storedSnapshotAt: string | null, incomingSnapshotAt: string) {
  if (!storedSnapshotAt) return true;
  return new Date(incomingSnapshotAt).getTime() > new Date(storedSnapshotAt).getTime();
}

// ---------------------------------------------------------------------------
// Subscription → our row
// ---------------------------------------------------------------------------

/** The parts of a Stripe subscription we read (API 2025-03-31 and later). */
export interface StripeSubscriptionLike {
  id: string;
  status: string;
  livemode: boolean;
  customer: string | { id: string };
  cancel_at: number | null;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  trial_end: number | null;
  metadata: Record<string, string> | null;
  items: {
    data: Array<{
      current_period_end: number;
      price: {
        lookup_key: string | null;
        recurring: { interval: string; interval_count: number } | null;
        /** Expanded (`items.data.price.product`) to read `metadata.zettl_plan`. */
        product:
          string | { id: string; metadata?: Record<string, string> | null; deleted?: boolean };
      };
    }>;
  };
}

export interface SubscriptionRow {
  id: string;
  owner_user_id: string;
  stripe_customer_id: string;
  plan_id: PlanId;
  billing_interval: BillingInterval;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  cancel_at: string | null;
  canceled_at: string | null;
  trial_end: string | null;
  livemode: boolean;
  snapshot_at: string;
}

const iso = (unix: number | null | undefined) =>
  unix === null || unix === undefined ? null : new Date(unix * 1000).toISOString();

export function customerIdOf(customer: string | { id: string }): string {
  return typeof customer === "string" ? customer : customer.id;
}

type Item = StripeSubscriptionLike["items"]["data"][number];

/**
 * Which plan an item sells. First the product's `metadata.zettl_plan` with the
 * price's own interval (survives a price losing or changing its lookup key),
 * then the lookup key.
 */
export function planOfItem(item: Item): { plan: PlanId; interval: BillingInterval } | null {
  const product = item.price.product;
  const tagged = typeof product === "string" ? undefined : product.metadata?.zettl_plan;
  const interval = item.price.recurring?.interval;
  if (isPlanId(tagged) && isInterval(interval) && item.price.recurring?.interval_count === 1) {
    return { plan: tagged, interval };
  }
  return parseLookupKey(item.price.lookup_key);
}

/**
 * Map a freshly fetched subscription to our row. When no item names a Zettl
 * plan, `stored` (the row we already have) keeps its plan and interval, so a
 * cancellation or a failed payment is still mirrored. Returns an error string
 * when the subscription isn't one of ours at all, or has no owner.
 */
export function subscriptionRow(
  sub: StripeSubscriptionLike,
  ownerUserId: string | null,
  snapshotAt: Date,
  stored: { plan_id: PlanId; billing_interval: BillingInterval } | null = null
): { row: SubscriptionRow } | { error: string } {
  const named = sub.items.data.map(planOfItem).find((x) => x !== null) ?? null;
  const plan =
    named ?? (stored ? { plan: stored.plan_id, interval: stored.billing_interval } : null);
  if (!plan) return { error: "no Zettl plan on any item (product metadata or lookup key)" };
  if (!ownerUserId) return { error: "no owner (billing_customers or metadata.user_id)" };

  const periodEnds = sub.items.data.map((i) => i.current_period_end).filter((n) => n > 0);
  return {
    row: {
      id: sub.id,
      owner_user_id: ownerUserId,
      stripe_customer_id: customerIdOf(sub.customer),
      plan_id: plan.plan,
      billing_interval: plan.interval,
      status: sub.status,
      current_period_end: periodEnds.length ? iso(Math.min(...periodEnds)) : null,
      cancel_at_period_end: sub.cancel_at_period_end,
      cancel_at: iso(sub.cancel_at),
      canceled_at: iso(sub.canceled_at),
      trial_end: iso(sub.trial_end),
      livemode: sub.livemode,
      snapshot_at: snapshotAt.toISOString(),
    },
  };
}

/**
 * Stripe statuses that still bill, or may bill again: a customer with one of
 * these must not get a second subscription from Checkout.
 */
export const BLOCKING_STRIPE_STATUSES: readonly string[] = [
  "active",
  "trialing",
  "past_due",
  "unpaid",
  "paused",
];

export function hasBlockingSubscription(subs: Array<{ status: string }>): boolean {
  return subs.some((s) => BLOCKING_STRIPE_STATUSES.includes(s.status));
}

// ---------------------------------------------------------------------------
// Which companies a subscription covers (mirrors billing_link_organizations)
// ---------------------------------------------------------------------------

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID.test(value);
}

/** `metadata.organization_ids` is a comma-separated list (metadata values are strings). */
export function parseOrgIds(value: string | null | undefined): string[] {
  return [...new Set((value ?? "").split(",").map((s) => s.trim()))].filter(isUuid);
}

/**
 * The orgs a subscription ends up covering: the first `limit` eligible ones of
 * `requested`, in order. Eligible: owned by the subscription's owner; for a
 * test-mode subscription, accepting test mode; not validly held by another
 * entitling subscription. A holder whose owner handed the org over lets it go,
 * and a live subscription may take an org from a test-mode one. Every other
 * link is dropped. Mirrors billing_link_organizations().
 */
export function selectOrgsToLink(input: {
  requested: string[];
  ownedByOwner: string[];
  live: boolean;
  acceptsTestMode: string[];
  /**
   * Orgs held by another entitling subscription: that subscription's mode, and
   * whether its owner still owns the org (false after a handover).
   */
  heldElsewhere: Array<{ org: string; live: boolean; holderOwnsIt: boolean }>;
  limit: number;
}): string[] {
  const kept: string[] = [];
  for (const org of input.requested) {
    if (kept.length >= input.limit) break;
    if (kept.includes(org)) continue;
    if (!input.ownedByOwner.includes(org)) continue;
    if (!input.live && !input.acceptsTestMode.includes(org)) continue;
    const holder = input.heldElsewhere.find((h) => h.org === org);
    if (holder && holder.holderOwnsIt && !(input.live && !holder.live)) continue;
    kept.push(org);
  }
  return kept;
}

/**
 * The order a subscription is re-linked in, by the webhook and on ownership
 * changes alike: the checkout's orgs, then the ones it covers now (oldest link
 * first), then the owner's other companies (oldest membership first). Mirrors
 * billing_relink_subscription().
 */
export function orgsToRequest(
  checkoutOrgIds: string[],
  linkedOrgIds: string[],
  ownedOrgIds: string[]
): string[] {
  return [...new Set([...checkoutOrgIds, ...linkedOrgIds, ...ownedOrgIds])];
}

/** The current org first, then the caller's other companies, deduplicated. */
export function orgsForCheckout(currentOrgId: string, ownedOrgIds: string[], limit: number) {
  return [...new Set([currentOrgId, ...ownedOrgIds])].slice(0, Math.max(0, limit));
}

// ---------------------------------------------------------------------------
// Checkout details
// ---------------------------------------------------------------------------

/** The IČO typed into Checkout's custom field: 8 digits, or null. */
export function extractIco(
  customFields: Array<{
    key: string;
    numeric?: { value: string | null } | null;
    text?: { value: string | null } | null;
  }> | null
): string | null {
  const field = customFields?.find((f) => f.key === "ico");
  const raw = (field?.numeric?.value ?? field?.text?.value ?? "").replace(/\s+/g, "");
  return /^\d{8}$/.test(raw) ? raw : null;
}

/** Phase B of the zettl.cz move switches this to https://app.zettl.cz. */
/**
 * Where Stripe sends the customer back. Only our own app hosts: zettlapp.com
 * and app.zettl.cz, `pr-<N>.zettlapp.com` / `pr-<N>.zettl.cz` previews and
 * localhost; anything else gets `fallback`, the app origin (`appOrigin()` from
 * `_shared/app-origin.ts`: the `APP_URL` secret, zettlapp.com until phase B).
 */
export function returnBaseUrl(
  origin: string | null | undefined,
  fallback = "https://zettlapp.com"
): string {
  const SITE = fallback;
  if (!origin) return SITE;
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return SITE;
  }
  const host = url.hostname;
  const ours =
    (url.protocol === "https:" &&
      (host === "zettlapp.com" ||
        host === "app.zettl.cz" ||
        /^pr-\d+\.(zettlapp\.com|zettl\.cz)$/.test(host))) ||
    (url.protocol === "http:" && (host === "localhost" || host === "127.0.0.1"));
  return ours ? url.origin : SITE;
}

/** The app is Czech and English; Checkout, the Portal and the return page follow it. */
export type BillingLang = "cs" | "en";

export function billingLang(value: unknown): BillingLang {
  return value === "en" ? "en" : "cs";
}

/** The Settings → Předplatné page, with a `checkout` or `portal` marker. */
export function billingPageUrl(base: string, lang: BillingLang, marker: string): string {
  return `${base}/${lang}/settings/billing?${marker}`;
}

/** Whether a secret or restricted key is a live-mode key. */
export function isLiveKey(key: string): boolean {
  return /^(sk|rk)_live_/.test(key);
}
